<script setup lang="ts">
  // このエディターを利用する場合には、親コンポーネントに overflow: hidden;を指定しないでください。
  // position: sticky;を利用しているため、overflow: hidden;を指定するとメニューが固定されてます。
  // v-cardにはoverflow: hidden;が指定されているため、v-card内で利用する場合は注意してください。

  // eslint-disable-next-line import/no-named-as-default
  import StarterKit from "@tiptap/starter-kit";
  // eslint-disable-next-line import/no-named-as-default
  import Link from "@tiptap/extension-link";
  import { useEditor, EditorContent } from "@tiptap/vue-3";
  import { Color } from "@tiptap/extension-color";
  // eslint-disable-next-line import/no-named-as-default
  import TextStyle from "@tiptap/extension-text-style";
  // eslint-disable-next-line import/no-named-as-default
  import Underline from "@tiptap/extension-underline";
  import { usePlanEditStore } from "~/stores/PlanEditStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import IFrame from "~/utils/TiptapExtensions/IFrame";
  import ImageResize from "~/utils/TiptapExtensions/CustomImageResize";
  import MemberOnly from "~/utils/TiptapExtensions/MemberOnly";

  // カラーピッカーの色
  const PickerColors = [
    [
      // red
      "#FF0000",
      // orange
      "#FFA500",
      // yellow
      "#FFFF00",
    ],
    [
      // green
      "#008000",
      // cyan
      "#00FFFF",
      // blue
      "#0000FF",
    ],
    [
      // purple
      "#800080",
      // pink
      "#FFC0CB",
      // brown
      "#A52A2A",
    ],
    [
      // white
      "#FFFFFF",
      // gray
      "#808080",
      // black
      "#000000",
    ],
  ];

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  const planEditStore = usePlanEditStore();

  interface Props {
    value: string;
    readonly?: boolean;
    enableMemberOnly?: boolean; // 会員限定要素を挿入するボタンを表示するかどうか
    enableHeadings?: boolean; // 見出しH1、H2、H3の挿入を許可するかどうか。falseの場合はH3のみ許可
    uploadImageFile?: (f: File) => Promise<string>; // 画像アップロード処理をカスタムしたい場合は、この関数を渡す
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:value", value: string): void;
  }
  const emit = defineEmits<Emits>();

  const container = ref<HTMLDivElement>();

  const showVideoDialog = ref(false);
  const inputVideoUrl = ref<string>("");
  const showUrlDialog = ref(false);
  const inputUrl = ref<string>("");

  const showColorPicker = ref(false);

  const pickColor = computed({
    get: () => {
      if (editor.value == null) return "#FFFFFF";
      return editor.value.getAttributes("textStyle").color ?? "#FFFFFF";
    },
    set: value => {
      if (editor.value == null) return;
      editor.value.chain().focus().setColor(value).run();
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Underline,
      Color,
      ImageResize.configure({
        inline: true, // imgがpタグで囲まれていても表示できるようにする
      }),
      IFrame,
      Link.configure({
        openOnClick: false,
      }),
      MemberOnly,
    ],
    content: props.value,
    onUpdate: () => {
      emit("update:value", editor.value?.getHTML() ?? "");
    },
  });

  const insertMemberOnly = () => {
    if (editor.value == null) return;
    editor.value
      .chain()
      .focus() // エディタにフォーカス
      .setMemberOnly() // 会員限定要素を挿入
      .createParagraphNear() // 新しい段落を改行として挿入
      .run(); // 実行
  };

  const onClickImageButton = () => {
    if (container.value == null || editor.value == null) return;

    let fileInput = container.value.querySelector("input.ql-image-input[type=file]") as HTMLInputElement | null;
    if (fileInput === null) {
      // inputがない場合は作成する
      fileInput = document.createElement("input");
      if (fileInput == null) return;
      fileInput.setAttribute("type", "file");
      fileInput.setAttribute("accept", acceptableImgExtentions.join(","));
      fileInput.classList.add("ql-image-input");
      fileInput.addEventListener("change", async () => {
        try {
          if (fileInput == null) return;
          if (fileInput.files != null && fileInput.files[0] != null && editor.value != null) {
            let url = "";
            if (props.uploadImageFile != null) {
              url = await props.uploadImageFile(fileInput.files[0]);
            } else {
              url = await planEditStore.uploadPlanDetailImageFile(fileInput.files[0]);
            }
            if (!url || url.length === 0) {
              throw new Error("画像のアップロードに失敗");
            }
            editor.value.chain().focus().setImage({ src: url }).run();
          }
        } catch (error: any) {
          snackBarStore.set("画像のアップロードに失敗しました", "error");
        }
      });

      container.value.appendChild(fileInput);
    } else {
      // inputがすでにある場合は、データをクリアする。同じファイルを選択した際にchangeイベントが発火しないため。
      fileInput.value = "";
    }
    if (fileInput == null) return;
    fileInput.click();
  };

  const onClickVideoButton = () => {
    if (editor.value == null) return;
    inputVideoUrl.value = "";
    showVideoDialog.value = true;
  };

  const onInsertVideo = () => {
    if (editor.value == null) return;
    editor.value.chain().focus().setIframe({ src: inputVideoUrl.value }).run();
  };

  const onClickSetLinkButton = () => {
    if (editor.value == null) return;
    inputUrl.value = editor.value.getAttributes("link").href;
    showUrlDialog.value = true;
  };

  const onInsertUrl = () => {
    if (editor.value == null) return;
    if (inputUrl.value) {
      editor.value.chain().focus().extendMarkRange("link").setLink({ href: inputUrl.value }).run();
    } else {
      editor.value.chain().focus().extendMarkRange("link").unsetLink().run();
    }
  };
</script>

<template>
  <div ref="container" class="editor-container">
    <div v-if="editor" class="editor-menu d-flex flex-wrap mt-2">
      <template v-if="enableHeadings">
        <v-btn
          :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
        >
          見出し1
        </v-btn>
        <v-btn
          :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
        >
          見出し2
        </v-btn>
        <v-btn
          :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        >
          見出し3
        </v-btn>
      </template>
      <template v-else>
        <v-btn
          :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
        >
          見出し
        </v-btn>
      </template>
      <v-btn
        :class="{ 'is-active': editor.isActive('paragraph') }"
        @click="editor.chain().focus().setParagraph().run()"
      >
        本文
      </v-btn>
      <v-btn class="color-button" @click="showColorPicker = !showColorPicker">
        <div class="color-button-fill"></div>
      </v-btn>
      <v-btn
        :disabled="!editor.can().chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <v-icon icon="$mdiFormatBold" />
      </v-btn>
      <!-- <v-btn
        :disabled="!editor.can().chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <v-icon icon="$mdiFormatItalic" />
      </v-btn> -->
      <v-btn
        :disabled="!editor.can().chain().focus().toggleUnderline().run()"
        :class="{ 'is-active': editor.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <v-icon icon="$mdiFormatUnderline" />
      </v-btn>
      <v-btn
        :disabled="!editor.can().chain().focus().toggleStrike().run()"
        :class="{ 'is-active': editor.isActive('strike') }"
        @click="editor.chain().focus().toggleStrike().run()"
      >
        <v-icon icon="$mdiFormatStrikethrough" />
      </v-btn>
      <v-btn
        :class="{ 'is-active': editor.isActive('horizontalRule') }"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        <v-icon icon="$mdiBorderHorizontal" />
      </v-btn>

      <v-btn :class="{ 'is-active': editor.isActive('link') }" @click="onClickSetLinkButton">
        <v-icon icon="$mdiLink" />
      </v-btn>
      <v-btn
        :class="{ 'is-active': editor.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <v-icon icon="$mdiFormatListBulleted" />
      </v-btn>
      <v-btn
        :class="{ 'is-active': editor.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <v-icon icon="$mdiFormatListNumbered" />
      </v-btn>
      <v-btn v-if="enableMemberOnly" :class="{ 'is-active': editor.isActive('memberOnly') }" @click="insertMemberOnly">
        会員限定
      </v-btn>
      <v-btn @click="onClickImageButton">
        <v-icon icon="$mdiImage"></v-icon>
      </v-btn>
      <v-btn @click="onClickVideoButton">
        <v-icon icon="$mdiVideo"></v-icon>
      </v-btn>
      <v-btn :disabled="!editor.can().chain().focus().undo().run()" @click="editor.chain().focus().undo().run()">
        <v-icon icon="$mdiUndo" />
      </v-btn>
      <v-btn :disabled="!editor.can().chain().focus().redo().run()" @click="editor.chain().focus().redo().run()">
        <v-icon icon="$mdiRedo" />
      </v-btn>
    </div>
    <EditorContent :editor="editor" class="focus:outline-none mt-8" />
    <AtomsInsertUrlDialog
      v-model:is-open="showVideoDialog"
      v-model:input-url="inputVideoUrl"
      title="Fincsに投稿済みの動画のURL、またはyoutube、vimeoのURLを入力してください"
      placeholder="https://fincs.jp/plan/000000000000000/movie/000"
      type="video"
      @click-ok="onInsertVideo"
    />
    <AtomsInsertUrlDialog
      v-model:is-open="showUrlDialog"
      v-model:input-url="inputUrl"
      title="リンクしたいURLを入力してください"
      placeholder="https://...."
      @click-ok="onInsertUrl"
    />
    <v-dialog v-model="showColorPicker" scrim="black" max-width="290">
      <v-color-picker v-model="pickColor" hide-inputs show-swatches :swatches="PickerColors" />
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
  .ql-container {
    font-size: 18px;
  }
  :deep(.ql-image-input) {
    display: none;
  }

  .editor-container::v-deep(.ProseMirror) {
    outline: none;
    overflow: auto;
  }

  .editor-menu {
    background-color: rgb(var(--v-theme-monochrome-04));
    position: sticky;
    position: -webkit-sticky;
    top: 10px;
    z-index: 100;
    .v-btn {
      margin-right: 4px;
      /* width: 32px; */
      min-width: 32px;
      /* height: 32px; */
      margin: 4px 4px;
      padding: 0px 10px;

      background-color: #333333;
      &.is-active {
        background-color: #119cf6;
      }
      .v-btn__content {
        margin: 10px;
      }
    }
  }

  .color-button-fill {
    background-color: v-bind(pickColor);
    width: 22px;
    height: 22px;
    border-radius: 10%;
  }
</style>
